import { compact } from 'lodash';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  AugmentedCreateHypotheticalGratInput,
  AugmentedCreateHypotheticalTransferAssetInput,
  AugmentedUpdateHypotheticalGratInput,
  HypotheticalGratSourceKind,
  HypotheticalTransferAssetKind,
  HypotheticalTransferFundingSourceKind,
  TransferMethod,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { DollarPercentValues } from '../EstateWaterfallHypotheticalTransfers/DollarPercentButtonGroup';
import { CommonHypotheticalTransferFormShape } from '../EstateWaterfallHypotheticalTransfers/estateWaterfallHypotheticalTransfers.types';
import { HypotheticalGratFormShape } from './EstateWaterfallHypotheticalGratModal.types';
import { EstateWaterfallHypotheticalGratModalQuery } from './graphql/EstateWaterfallHypotheticalGratModal.generated';

export function getModalHeading({ isCreating }: { isCreating: boolean }) {
  if (isCreating) {
    return 'Create hypothetical GRAT';
  }

  return 'Edit hypothetical GRAT';
}

export function getButtonText({ isCreating }: { isCreating: boolean }) {
  if (isCreating) {
    return 'Create transfer';
  }

  return 'Save changes';
}

interface MapQueryDataToFormDataInput {
  data: EstateWaterfallHypotheticalGratModalQuery;
  hypotheticalWaterfallId: string;
  initialSourceId?: string;
  gratId?: string;
  displayName?: string;
  sourceId?: string;
  transferMethod?: TransferMethod;
  simulatedGratId?: string;
}

export const mapQueryDataToFormData = ({
  data,
  hypotheticalWaterfallId,
  initialSourceId,
  gratId,
  displayName,
  sourceId: externalSourceId,
  simulatedGratId: simulatedGratIdExternal,
}: MapQueryDataToFormDataInput): HypotheticalGratFormShape | undefined => {
  const waterfall = getNodes(data.estateWaterfalls)[0];
  const existingGrat = getNodes(waterfall?.hypotheticalGrats).find(
    (hg) => hg.id === gratId
  );

  if (!existingGrat) {
    const defaultValues = getDefaultValues({
      sourceId: initialSourceId || externalSourceId,
      displayName,
    });
    return defaultValues;
  }

  const sourceId = (() => {
    switch (existingGrat.sourceKind) {
      case HypotheticalGratSourceKind.Entity:
        return existingGrat.sourceEntity?.id ?? '';
      case HypotheticalGratSourceKind.Individual:
        return existingGrat.sourceIndividual?.id ?? '';
      default:
        return '';
    }
  })();

  // Prefer to use the value from the form, but fall back to the value from the query
  const simulatedGratId =
    simulatedGratIdExternal || existingGrat.simulatedGrat.id;

  return {
    displayName: existingGrat.displayName,
    hypotheticalWaterfallId: hypotheticalWaterfallId,
    simulatedGratId,
    sourceId: sourceId,
    fundingKind: getFormFundingKindFromAPI(existingGrat.funding?.kind),
    proRataPattern: existingGrat.funding?.proRataAmount
      ? DollarPercentValues.DOLLAR
      : DollarPercentValues.PERCENT,
    proRataAmount: existingGrat.funding?.proRataAmount ?? null,
    proRataPercent: existingGrat.funding?.proRataPercentage ?? null,
    applyEntityTaxDiscount: existingGrat.taxDiscount?.greaterThan(0)
      ? true
      : false,
    entityTaxDiscountRate: existingGrat.taxDiscount,
    assetsPattern:
      existingGrat.funding?.kind ===
      HypotheticalTransferFundingSourceKind.SpecificAssetsPercentage
        ? 'percent'
        : 'amount',
    selectedAssetClassesAndBusinessIds: compact(
      getNodes(existingGrat.funding?.assets).map(
        (asset) => asset.assetClass?.id || asset.ownedBusiness?.id
      )
    ),
    selectedAssetClassAndBusinessValues: getNodes(
      existingGrat.funding?.assets
    ).reduce(
      (acc, asset) => {
        if (asset.assetClass?.id) {
          acc[asset.assetClass?.id] = {
            amount: asset.valueAmount ?? null,
            percent: asset.valuePercentage ?? null,
          };
        }

        if (asset.ownedBusiness?.id) {
          acc[asset.ownedBusiness?.id] = {
            amount: asset.valueAmount ?? null,
            percent: asset.valuePercentage ?? null,
          };
        }

        return acc;
      },
      {} as CommonHypotheticalTransferFormShape['selectedAssetClassAndBusinessValues']
    ),
    forceLiquidationOnTransfer:
      existingGrat.funding?.transferMethod == TransferMethod.LiquidateToCash
        ? true
        : false,
  };
};

export const mapFormDataToCreateInput = (
  formData: HypotheticalGratFormShape,
  waterfallId: string,
  householdId: string
): AugmentedCreateHypotheticalGratInput => {
  const fundingKind = getFundingKind(formData);
  const sourceKind = getSourceKind(formData.sourceId);

  const input: AugmentedCreateHypotheticalGratInput = {
    create: {
      householdID: householdId,
      displayName: formData.displayName,
      hypotheticalWaterfallID: waterfallId,
      simulatedGratID: formData.simulatedGratId,
      sourceKind,
      sourceIndividualID:
        sourceKind === HypotheticalGratSourceKind.Individual
          ? formData.sourceId
          : undefined,
      sourceEntityID:
        sourceKind === HypotheticalGratSourceKind.Entity
          ? formData.sourceId
          : undefined,
      taxDiscount: formData.applyEntityTaxDiscount
        ? formData.entityTaxDiscountRate
        : null,
    },
    withFunding: {
      create: {
        householdID: householdId,
        kind: fundingKind,
        proRataAmount:
          fundingKind === HypotheticalTransferFundingSourceKind.ProRataAmount
            ? formData.proRataAmount
            : null,
        proRataPercentage:
          fundingKind ===
          HypotheticalTransferFundingSourceKind.ProRataPercentage
            ? formData.proRataPercent
            : null,
        transferMethod: formData.forceLiquidationOnTransfer
          ? TransferMethod.LiquidateToCash
          : TransferMethod.InKind,
      },
      withAssets: mapFormDataToAssetInputs(formData, householdId),
    },
  };

  return input;
};

export const mapFormDataToUpdateInput = (
  formData: HypotheticalGratFormShape,
  hypotheticalGratId: string,
  hypotheticalGratFundingId: string,
  householdId: string
): AugmentedUpdateHypotheticalGratInput => {
  const fundingKind = getFundingKind(formData);
  const sourceKind = getSourceKind(formData.sourceId);

  const input: AugmentedUpdateHypotheticalGratInput = {
    id: hypotheticalGratId,
    update: {
      displayName: formData.displayName,
      simulatedGratID: formData.simulatedGratId,
      sourceKind,
      sourceIndividualID:
        sourceKind === HypotheticalGratSourceKind.Individual
          ? formData.sourceId
          : undefined,
      sourceEntityID:
        sourceKind === HypotheticalGratSourceKind.Entity
          ? formData.sourceId
          : undefined,
      taxDiscount: formData.applyEntityTaxDiscount
        ? formData.entityTaxDiscountRate
        : undefined,
      clearTaxDiscount: formData.applyEntityTaxDiscount ? undefined : true,
    },
    updateFunding: {
      id: hypotheticalGratFundingId,
      update: {
        kind: fundingKind,
        clearAssets: true,
        proRataAmount:
          fundingKind === HypotheticalTransferFundingSourceKind.ProRataAmount
            ? formData.proRataAmount
            : null,
        proRataPercentage:
          fundingKind ===
          HypotheticalTransferFundingSourceKind.ProRataPercentage
            ? formData.proRataPercent
            : null,
        clearProRataAmount:
          fundingKind !== HypotheticalTransferFundingSourceKind.ProRataAmount,
        clearProRataPercentage:
          fundingKind !==
          HypotheticalTransferFundingSourceKind.ProRataPercentage,
        transferMethod: formData.forceLiquidationOnTransfer
          ? TransferMethod.LiquidateToCash
          : TransferMethod.InKind,
      },
      withAssets: mapFormDataToAssetInputs(formData, householdId),
    },
  };

  return input;
};

interface GetDefaultValuesInput {
  sourceId?: string;
  displayName?: string;
}

export function getDefaultValues(
  input?: GetDefaultValuesInput
): HypotheticalGratFormShape {
  return {
    displayName: input?.displayName ?? '',
    hypotheticalWaterfallId: '',
    simulatedGratId: '',
    sourceId: input?.sourceId ?? '',
    fundingKind: 'proRata',
    proRataPattern: DollarPercentValues.DOLLAR,
    proRataAmount: null,
    proRataPercent: null,
    assetsPattern: 'amount',
    selectedAssetClassesAndBusinessIds: [],
    selectedAssetClassAndBusinessValues: {},
    applyEntityTaxDiscount: false,
    entityTaxDiscountRate: undefined,
    forceLiquidationOnTransfer: false,
  };
}

export function getGratOptions(
  data: EstateWaterfallHypotheticalGratModalQuery,
  gratId?: string
): TypeaheadSelectInputOption<string>[] {
  const linkedGrats = getNodes(
    getNodes(data.estateWaterfalls)[0]?.hypotheticalGrats
  )
    // Do not disable the option of the GRAT transfer we are editing
    .filter((hg) => hg.id !== gratId)
    .map((hg) => hg.simulatedGrat.id);

  return getNodes(data.gratOptions).map((grat) => {
    const isLinked = linkedGrats.includes(grat.id);
    const hasNoTermDuration = !(
      'termDurationYears' in grat.subtype && grat.subtype.termDurationYears
    );

    const isDisabled = isLinked || hasNoTermDuration;

    const endLabel = (() => {
      if (isLinked) {
        return 'This GRAT is already linked to a hypothetical GRAT';
      }

      if (hasNoTermDuration) {
        return 'This GRAT does not have a term duration';
      }

      return undefined;
    })();

    return {
      value: grat.id,
      display: grat.subtype.displayName,
      disabled: isDisabled,
      endLabel,
    };
  });
}

export function getFundingIds(data: EstateWaterfallHypotheticalGratModalQuery) {
  const waterfall = getNodes(data.estateWaterfalls)[0];
  return getNodes(waterfall?.hypotheticalGrats).map((hg) => {
    return {
      value: hg.funding?.id,
      gratId: hg.id,
    };
  });
}

export function getSourceEntityOptions(
  data: EstateWaterfallHypotheticalGratModalQuery
): TypeaheadSelectInputOption<string>[] {
  return getNodes(data.sourceEntityOptions).map((entity) => ({
    value: entity.id,
    display: entity.subtype.displayName,
    groupName: 'Entities',
  }));
}

export function getSourceIndividualOptions(
  data: EstateWaterfallHypotheticalGratModalQuery
): TypeaheadSelectInputOption<string>[] {
  const household = getNodes(data.households)[0];
  const possibleGrantors = household?.sourceIndividualOptions ?? [];

  return possibleGrantors.map((individual) => ({
    value: individual.id,
    display: individual.displayName,
    groupName: 'Individuals',
  }));
}

const getFormFundingKindFromAPI = (
  kind: HypotheticalTransferFundingSourceKind | undefined
): HypotheticalGratFormShape['fundingKind'] | null => {
  switch (kind) {
    case HypotheticalTransferFundingSourceKind.ProRataAmount:
      return 'proRata';
    case HypotheticalTransferFundingSourceKind.ProRataPercentage:
      return 'proRata';
    case HypotheticalTransferFundingSourceKind.SpecificAssetsAmount:
      return 'assets';
    case HypotheticalTransferFundingSourceKind.SpecificAssetsPercentage:
      return 'assets';
    default:
      return null;
  }
};

function getFundingKind(
  formData: HypotheticalGratFormShape
): HypotheticalTransferFundingSourceKind {
  if (formData.fundingKind === 'proRata') {
    if (formData.proRataPattern === 'amount') {
      return HypotheticalTransferFundingSourceKind.ProRataAmount;
    } else if (formData.proRataPattern === 'percent') {
      return HypotheticalTransferFundingSourceKind.ProRataPercentage;
    } else {
      throw new Error(`Unhandled proRata pattern: ${formData.proRataPattern}`);
    }
  }

  if (formData.fundingKind === 'assets') {
    if (formData.assetsPattern === 'amount') {
      return HypotheticalTransferFundingSourceKind.SpecificAssetsAmount;
    } else if (formData.assetsPattern === 'percent') {
      return HypotheticalTransferFundingSourceKind.SpecificAssetsPercentage;
    } else {
      throw new Error(`Unhandled assets pattern: ${formData.assetsPattern}`);
    }
  }

  throw new Error(`Unhandled funding kind: ${formData.fundingKind}`);
}

const getSourceKind = (sourceEntityId: string) => {
  return getPulidKind(sourceEntityId) === PulidKind.Entity
    ? HypotheticalGratSourceKind.Entity
    : HypotheticalGratSourceKind.Individual;
};

const mapFormDataToAssetInputs = (
  formData: HypotheticalGratFormShape,
  householdId: string
): AugmentedCreateHypotheticalTransferAssetInput[] => {
  if (formData.fundingKind !== 'assets') {
    return [];
  }

  return formData.selectedAssetClassesAndBusinessIds.map((id) => {
    const value = formData.selectedAssetClassAndBusinessValues[id];
    if (!value) {
      throw new Error(`Missing value for asset/business ${id}`);
    }

    const kind =
      getPulidKind(id) === PulidKind.AssetClass
        ? HypotheticalTransferAssetKind.AssetClass
        : HypotheticalTransferAssetKind.BusinessOwnership;

    const createAssetInput: AugmentedCreateHypotheticalTransferAssetInput = {
      create: {
        householdID: householdId,
        kind,
        assetClassID:
          kind === HypotheticalTransferAssetKind.AssetClass ? id : undefined,
        ownedBusinessID:
          kind === HypotheticalTransferAssetKind.BusinessOwnership
            ? id
            : undefined,
        valueAmount:
          formData.assetsPattern === 'amount' ? (value.amount ?? null) : null,
        valuePercentage:
          formData.assetsPattern === 'percent' ? (value.percent ?? null) : null,
      },
    };

    return createAssetInput;
  });
};
